import { getNextDayKey } from '@zag/calendar/models';

export interface RequestCoverage {
  /** ISO dates in the request window where headcount is met. */
  coveredDays: Set<string>;
  /** ISO dates in the request window where headcount is not yet met. */
  uncoveredDays: Set<string>;
  /** Approved-claim count per day across the full request window. Uncovered days are explicit `0` entries. */
  headcountPerDay: Record<string, number>;
  /** Covered days as percent of total days, half-up rounded to 1 decimal place, clamped 0-100. */
  fulfilmentPercent: number;
}

export interface CoverageRequestInput {
  startDate: string;
  endDate: string;
  headcount: number;
}

export interface CoverageClaimInput {
  startDate: string;
  endDate: string;
}

/**
 * Compute per-day coverage of a labour-pool request from its approved claims.
 *
 * Days are inclusive ISO calendar date strings (YYYY-MM-DD). Claims are clipped
 * to the request window before counting; claims entirely outside the window are
 * ignored. A day is covered when the count of overlapping approved claims is
 * `>= request.headcount`. `headcount <= 0` is treated as vacuously fulfilled.
 *
 * Pure and isomorphic — safe in both Node (API) and browser (UI) bundles.
 */
export function computeRequestCoverage(request: CoverageRequestInput, approvedClaims: readonly CoverageClaimInput[]): RequestCoverage {
  const allDays = listDateKeysInclusive(request.startDate, request.endDate);
  const counts = new Map<string, number>();
  for (const day of allDays) counts.set(day, 0);

  for (const claim of approvedClaims) {
    const clipStart = claim.startDate > request.startDate ? claim.startDate : request.startDate;
    const clipEnd = claim.endDate < request.endDate ? claim.endDate : request.endDate;
    for (const day of listDateKeysInclusive(clipStart, clipEnd)) {
      counts.set(day, (counts.get(day) ?? 0) + 1);
    }
  }

  const coveredDays = new Set<string>();
  const uncoveredDays = new Set<string>();
  const headcountPerDay: Record<string, number> = {};
  for (const day of allDays) {
    const count = counts.get(day) ?? 0;
    headcountPerDay[day] = count;
    if (count >= request.headcount) coveredDays.add(day);
    else uncoveredDays.add(day);
  }

  const fulfilmentPercent = computeFulfilmentPercent(coveredDays.size, allDays.length, request.headcount);
  return { coveredDays, uncoveredDays, headcountPerDay, fulfilmentPercent };
}

function listDateKeysInclusive(start: string, end: string): string[] {
  if (start > end) return [];
  const out: string[] = [];
  let day: string | null = start;
  while (day !== null && day <= end) {
    out.push(day);
    day = getNextDayKey(day);
  }
  return out;
}

function computeFulfilmentPercent(coveredCount: number, totalDays: number, headcount: number): number {
  if (headcount <= 0) return 100;
  if (totalDays <= 0) return 0;
  const raw = (coveredCount / totalDays) * 100;
  const rounded = Math.round(raw * 10) / 10;
  return Math.min(100, Math.max(0, rounded));
}

/**
 * Inclusive count of calendar days in the intersection of [aStart, aEnd] and [bStart, bEnd].
 * Returns 0 if the ranges do not overlap.
 */
export function computeIntersectionDays(aStart: string, aEnd: string, bStart: string, bEnd: string): number {
  const intStart = aStart > bStart ? aStart : bStart;
  const intEnd = aEnd < bEnd ? aEnd : bEnd;
  if (intEnd < intStart) return 0;
  return Math.floor((new Date(intEnd).getTime() - new Date(intStart).getTime()) / 86400000) + 1;
}

/**
 * Returns true when every skill in `requiredSkills` is present in `offerSkills`.
 * Returns true immediately when `requiredSkills` is empty (no skill requirement).
 */
export function allSkillsSatisfied(offerSkills: string[], requiredSkills: string[]): boolean {
  if (requiredSkills.length === 0) return true;
  return requiredSkills.every(s => offerSkills.includes(s));
}
